import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Retry strategy:
// - Anonymous probes (initial /auth/me, refreshUser): short retries so a black
//   loading screen doesn't last forever (2 attempts × 400ms).
// - Active user actions (login, password reset): aggressive retries so a single
//   Cloudflare 520 / mobile network blip doesn't force the user to type their
//   password again (5 attempts × 800ms backoff).
const TRANSIENT_STATUS = new Set([502, 503, 504, 520, 521, 522, 523, 524, 408]);
async function _retrying(fn, attempts = 2, baseDelayMs = 400) {
  let lastErr;
  for (let i = 1; i <= attempts; i++) {
    try { return await fn(); }
    catch (err) {
      lastErr = err;
      const transient = !err.response || TRANSIENT_STATUS.has(err.response.status);
      if (!transient || i === attempts) throw err;
      await new Promise(r => setTimeout(r, baseDelayMs * i));
    }
  }
  throw lastErr;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null = checking, false = not logged in
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    _retrying(() => axios.get(`${API}/auth/me`, { withCredentials: true }))
      .then(r => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    // User-initiated action — be aggressive about retries so a transient 520
    // doesn't make them type their password 4 times. 5 × 800ms backoff covers
    // ~6 seconds of edge-network trouble, after which we surface a clear error.
    await _retrying(
      () => axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true }),
      5, 800,
    );
    const { data } = await _retrying(
      () => axios.get(`${API}/auth/me`, { withCredentials: true }),
      5, 800,
    );
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await _retrying(() => axios.get(`${API}/auth/me`, { withCredentials: true }));
      setUser(data);
      return data;
    } catch (_) { return null; }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
